import { useMemo, useState } from 'react'
import {
  AlertCircle, AlertTriangle, Bell, CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, Clock3,
  FileText, Filter, Info as InfoIcon, Loader2, Mail, MailPlus, Paperclip, Plus, ShieldAlert, StickyNote, User, Users, X,
} from 'lucide-react'
import { Card } from '../atoms'
import { cn } from '../atoms'
import { IndicatorCard } from '../molecules/IndicatorCard'
import {
  useCalendarUsers, useCompleteRegulatoryEvent, useCreateRegulatoryEvent,
  useDeleteRegulatoryEvent, useRegulatoryEntities, useRegulatoryEvents, useRegulatoryStats,
  useUpdateRegulatoryEvent,
  EVENT_TYPE_LABELS, EVENT_TYPE_DESCRIPTIONS, FREQUENCY_LABELS, FREQUENCY_OPTIONS,
  SEVERITY_OPTIONS, SEVERITY_LABELS, SEVERITY_COLORS, SEVERITY_BADGE, SEVERITY_RANK,
  type CalendarAttachment, type CalendarRecipient, type CalendarUser, type EventSeverity,
  type RegulatoryEvent, type RegulatoryEventType, type RegulatoryFrequency,
} from '../../hooks/useRegulatoryCalendar'
import { useSuccessToast, useErrorToast } from '../atoms/Toaster'

const statusLabels: Record<string, string> = {
  PENDING: 'Pendiente', IN_PROGRESS: 'En gestión', COMPLETED: 'Cumplido',
  OVERDUE: 'Vencido', CANCELLED: 'Cancelado',
}

const eventTypeIcons: Record<RegulatoryEventType, any> = {
  REGULATORY: ShieldAlert,
  REMINDER: Bell,
  NOTE: StickyNote,
  EXTERNAL_EMAIL: Mail,
}

const eventTypeColors: Record<RegulatoryEventType, string> = {
  REGULATORY: 'bg-blue-50 text-blue-700 border-blue-200',
  REMINDER: 'bg-amber-50 text-amber-700 border-amber-200',
  NOTE: 'bg-slate-100 text-slate-600 border-slate-200',
  EXTERNAL_EMAIL: 'bg-purple-50 text-purple-700 border-purple-200',
}

const eventTypeDayColors: Record<RegulatoryEventType, string> = {
  REGULATORY: 'bg-blue-50 text-blue-700',
  REMINDER: 'bg-amber-50 text-amber-700',
  NOTE: 'bg-slate-100 text-slate-600',
  EXTERNAL_EMAIL: 'bg-purple-50 text-purple-700',
}

const severityIcons: Record<EventSeverity, any> = {
  CRITICO_MAXIMO: AlertCircle,
  CRITICO: AlertTriangle,
  ALTO: ShieldAlert,
  MEDIO: Bell,
  BAJO: InfoIcon,
  INFORMATIVO: Mail,
}

export function RegulatoryCalendarPage() {
  const [month, setMonth] = useState(() => new Date())
  const [entityId, setEntityId] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [severityFilter, setSeverityFilter] = useState<string>('')
  const [showForm, setShowForm] = useState(false)
  const [editingEvent, setEditingEvent] = useState<RegulatoryEvent | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<RegulatoryEvent | null>(null)
  const [dayPopover, setDayPopover] = useState<{ day: Date; events: RegulatoryEvent[]; anchor: { top: number; left: number } } | null>(null)

  const { data: entities } = useRegulatoryEntities()
  const { data: events, isLoading } = useRegulatoryEvents({
    entityId: entityId || undefined,
    type: typeFilter || undefined,
    severity: severityFilter || undefined,
  })
  const { data: stats } = useRegulatoryStats()
  const completeMutation = useCompleteRegulatoryEvent()
  const createMutation = useCreateRegulatoryEvent()
  const updateMutation = useUpdateRegulatoryEvent()
  const deleteMutation = useDeleteRegulatoryEvent()
  const successToast = useSuccessToast()
  const errorToast = useErrorToast()

  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1)
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0)
  const monthEvents = (events || []).filter(event => {
    const start = new Date(event.startDate)
    const nextDue = new Date(event.nextDueDate)
    return start.getFullYear() === month.getFullYear() && start.getMonth() === month.getMonth() ||
      nextDue.getFullYear() === month.getFullYear() && nextDue.getMonth() === month.getMonth()
  })

  const calendarDays = useMemo(() => {
    const firstDay = (monthStart.getDay() + 6) % 7
    const total = Math.ceil((firstDay + monthEnd.getDate()) / 7) * 7
    return Array.from({ length: total }, (_, index) => {
      const day = index - firstDay + 1
      return day > 0 && day <= monthEnd.getDate() ? new Date(month.getFullYear(), month.getMonth(), day) : null
    })
  }, [month.getFullYear(), month.getMonth()])

  const moveMonth = (amount: number) =>
    setMonth(current => new Date(current.getFullYear(), current.getMonth() + amount, 1))

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })

  const eventForDay = (day: Date | null) => {
    if (!day) return []
    return monthEvents.filter(event => {
      const start = new Date(event.startDate)
      const nextDue = new Date(event.nextDueDate)
      return start.getDate() === day.getDate() && start.getMonth() === day.getMonth() ||
        nextDue.getDate() === day.getDate() && nextDue.getMonth() === day.getMonth()
    })
  }

  const handleComplete = async (event: RegulatoryEvent) => {
    try {
      await completeMutation.mutateAsync(event.id)
      successToast(`Cumplimiento registrado. Próxima fecha actualizada.`)
    } catch {
      errorToast('No se pudo completar el evento')
    }
  }

  const handleDelete = async (event: RegulatoryEvent) => {
    if (!confirm(`¿Cancelar el evento "${event.title}"?`)) return
    try {
      await deleteMutation.mutateAsync(event.id)
      successToast('Evento cancelado')
      setSelectedEvent(null)
    } catch {
      errorToast('No se pudo cancelar el evento')
    }
  }

  const handleCreate = async (body: any) => {
    try {
      await createMutation.mutateAsync(body)
      setShowForm(false)
      successToast('Evento creado exitosamente')
    } catch (error: any) {
      errorToast(error?.message || 'No se pudo crear el evento')
    }
  }

  const handleUpdate = async (id: string, body: any) => {
    try {
      await updateMutation.mutateAsync({ id, body })
      setEditingEvent(null)
      setSelectedEvent(null)
      successToast('Evento actualizado')
    } catch (error: any) {
      errorToast(error?.message || 'No se pudo actualizar el evento')
    }
  }

  return (
    <div className="mx-auto max-w-[1700px] space-y-5">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950 dark:text-gray-100 md:text-3xl">
            Calendario corporativo
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
            Obligaciones regulatorias, recordatorios, notas y envíos a externos
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" /> Nuevo evento
        </button>
      </header>

      {/* Stat cards */}
      <section className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <SummaryCard label="Total activos" value={stats?.total || 0} icon={CalendarDays} tone="blue" />
        <SummaryCard label="Crítico/Máx" value={(stats?.bySeverity?.CRITICO_MAXIMO || 0) + (stats?.bySeverity?.CRITICO || 0)} icon={AlertCircle} tone="red" />
        <SummaryCard label="Alto" value={stats?.bySeverity?.ALTO || 0} icon={AlertTriangle} tone="amber" />
        <SummaryCard label="Medio/Bajo" value={(stats?.bySeverity?.MEDIO || 0) + (stats?.bySeverity?.BAJO || 0)} icon={Bell} tone="blue" />
        <SummaryCard label="Vencidos" value={stats?.overdue || 0} icon={ShieldAlert} tone="red" />
      </section>

      {/* Filtros */}
      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-gray-200">
          <Filter className="h-4 w-4 text-slate-400" /> Filtros
        </div>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">Todos los tipos</option>
          {(Object.keys(EVENT_TYPE_LABELS) as RegulatoryEventType[]).map(t => (
            <option key={t} value={t}>{EVENT_TYPE_LABELS[t]}</option>
          ))}
        </select>
        <select
          value={severityFilter}
          onChange={e => setSeverityFilter(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">Todas las severidades</option>
          {SEVERITY_OPTIONS.map(s => (
            <option key={s} value={s}>{SEVERITY_LABELS[s]}</option>
          ))}
        </select>
        <select
          value={entityId}
          onChange={e => setEntityId(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">Todas las entidades</option>
          {entities?.map(entity => (
            <option key={entity.id} value={entity.id}>
              {entity.acronym ? `${entity.acronym} · ` : ''}{entity.name}
            </option>
          ))}
        </select>
        <div className="sm:ml-auto text-xs text-slate-400">
          {events?.length || 0} eventos registrados
        </div>
      </section>

      {/* Layout: Calendario + lista lateral */}
      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.5fr_1fr]">
        <Card className="overflow-hidden rounded-xl border-slate-200 p-0 shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-gray-700">
            <button onClick={() => moveMonth(-1)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h2 className="text-base font-bold capitalize text-slate-900 dark:text-gray-100">
              {month.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })}
            </h2>
            <button onClick={() => moveMonth(1)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:border-gray-700 dark:bg-gray-800/50">
            {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
              <span key={d} className="py-3">{d}</span>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {calendarDays.map((day, index) => {
              const dayEvents = eventForDay(day)
              return (
                <div key={index} className="min-h-[105px] border-b border-r border-slate-100 p-2 dark:border-gray-800">
                  {day && (
                    <>
                      <span className={cn(
                        'mb-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs',
                        day.toDateString() === new Date().toDateString()
                          ? 'bg-primary-600 font-bold text-white'
                          : 'text-slate-500',
                      )}>
                        {day.getDate()}
                      </span>
                      <div className="space-y-1">
                        {(() => {
                          // Ordenar eventos del día: primero críticos/altos, luego medios/bajos/informativos
                          const sorted = [...dayEvents].sort((a, b) => {
                            const rankA = SEVERITY_RANK[a.severity || 'MEDIO'] ?? 0
                            const rankB = SEVERITY_RANK[b.severity || 'MEDIO'] ?? 0
                            return rankB - rankA
                          })
                          const visible = sorted.slice(0, 4)
                          const hidden = sorted.length - visible.length
                          return (
                            <>
                              {visible.map(event => {
                                return (
                                  <button
                                    key={event.id}
                                    onClick={() => setSelectedEvent(event)}
                                    title={`${event.title}${event.entity ? ` (${event.entity.acronym || event.entity.name})` : ''} · ${SEVERITY_LABELS[event.severity || 'MEDIO']}`}
                                    className={cn(
                                      'block w-full truncate rounded border px-1.5 py-1 text-left text-[10px] font-medium hover:ring-2 hover:ring-primary-200',
                                      event.computedStatus === 'OVERDUE'
                                        ? 'bg-red-50 text-red-700 border-red-200'
                                        : eventTypeDayColors[event.type] || 'bg-blue-50 text-blue-700 border-blue-200',
                                      (event.severity === 'CRITICO_MAXIMO' || event.severity === 'CRITICO') && 'border-l-2 border-l-red-600',
                                      event.severity === 'ALTO' && 'border-l-2 border-l-orange-500',
                                    )}
                                  >
                                    {event.entity?.acronym || event.title}
                                  </button>
                                )
                              })}
                              {hidden > 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                                    setDayPopover({
                                      day: day,
                                      events: sorted,
                                      anchor: { top: rect.bottom + 4, left: rect.left },
                                    })
                                  }}
                                  className="text-[10px] font-medium text-slate-500 hover:text-primary-600 hover:underline"
                                  title={`Ver ${hidden} evento${hidden > 1 ? 's' : ''} más de este día`}
                                >
                                  +{hidden} más
                                </button>
                              )}
                            </>
                          )
                        })()}
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </Card>

        {/* Sidebar: Próximos vencimientos */}
        <Card className="rounded-xl border-slate-200 p-0 shadow-sm">
          <div className="border-b border-slate-200 px-5 py-4 dark:border-gray-700">
            <h2 className="text-base font-bold text-slate-900 dark:text-gray-100">Próximos eventos</h2>
            <p className="mt-1 text-xs text-slate-500">Ordenados por fecha de vencimiento</p>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-gray-800 max-h-[600px] overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
              </div>
            ) : !events?.length ? (
              <p className="px-5 py-12 text-center text-sm text-slate-500">No hay eventos registrados</p>
            ) : (
              events.slice(0, 12).map(event => {
                const Icon = eventTypeIcons[event.type] || CalendarDays
                const isOverdue = event.computedStatus === 'OVERDUE'
                const severity = event.severity || 'MEDIO'
                return (
                  <div key={event.id} className="flex items-start gap-3 px-5 py-4 hover:bg-slate-50 dark:hover:bg-gray-800/50">
                    <div className={cn(
                      'mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border',
                      isOverdue ? 'bg-red-50 text-red-600 border-red-200' : eventTypeColors[event.type],
                    )}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-semibold text-slate-800 dark:text-gray-100">{event.title}</p>
                        <span className={cn(
                          'inline-flex shrink-0 items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide',
                          SEVERITY_BADGE[severity],
                        )}>
                          {SEVERITY_LABELS[severity]}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {event.entity?.acronym || event.entity?.name || EVENT_TYPE_LABELS[event.type]} ·{' '}
                        {FREQUENCY_LABELS[event.frequency]}
                        {event.startTime && ` · ${event.startTime}`}
                      </p>
                      <p className={cn(
                        'mt-1 text-xs font-medium',
                        isOverdue ? 'text-red-600' : 'text-slate-500',
                      )}>
                        {formatDate(event.nextDueDate)} · {statusLabels[event.computedStatus || event.status]}
                      </p>
                      {event.recipients && event.recipients.length > 0 && (
                        <p className="mt-1 text-xs text-slate-400">
                          <Users className="inline h-3 w-3 mr-0.5" /> {event.recipients.length} destinatario{event.recipients.length !== 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button
                        onClick={() => setSelectedEvent(event)}
                        className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                        title="Ver detalle"
                      >
                        <FileText className="h-4 w-4" />
                      </button>
                      {event.status !== 'COMPLETED' && event.status !== 'CANCELLED' && (
                        <button
                          onClick={() => handleComplete(event)}
                          disabled={completeMutation.isPending}
                          className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-50"
                          title="Marcar cumplido"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </Card>
      </section>

      {/* Form modal */}
      {showForm && (
        <EventForm
          entities={entities || []}
          users={[]}
          isPending={createMutation.isPending}
          onClose={() => setShowForm(false)}
          onSubmit={handleCreate}
        />
      )}

      {editingEvent && (
        <EventForm
          entities={entities || []}
          users={[]}
          isPending={updateMutation.isPending}
          initial={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSubmit={(body) => handleUpdate(editingEvent.id, body)}
        />
      )}

      {dayPopover && (
        <DayEventsPopover
          day={dayPopover.day}
          events={dayPopover.events}
          anchor={dayPopover.anchor}
          onClose={() => setDayPopover(null)}
          onSelectEvent={(event) => {
            setDayPopover(null)
            setSelectedEvent(event)
          }}
        />
      )}

      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          isPending={completeMutation.isPending || deleteMutation.isPending}
          onClose={() => setSelectedEvent(null)}
          onComplete={async () => { await handleComplete(selectedEvent); setSelectedEvent(null) }}
          onEdit={() => { setEditingEvent(selectedEvent); setSelectedEvent(null) }}
          onDelete={() => handleDelete(selectedEvent)}
        />
      )}
    </div>
  )
}

function SummaryCard({ label, value, icon: Icon, tone }: {
  label: string; value: number; icon: any; tone: 'blue' | 'red' | 'amber' | 'green' | 'slate' | 'purple'
}) {
  return <IndicatorCard label={label} value={value} icon={Icon} tone={tone} />
}

interface EventFormProps {
  entities: any[]
  users: CalendarUser[]
  isPending: boolean
  initial?: RegulatoryEvent
  onClose: () => void
  onSubmit: (body: any) => void
}

function EventForm({ entities, users, isPending, initial, onClose, onSubmit }: EventFormProps) {
  const [type, setType] = useState<RegulatoryEventType>(initial?.type || 'REGULATORY')
  const [entityId, setEntityId] = useState(initial?.entityId || '')
  const [title, setTitle] = useState(initial?.title || '')
  const [description, setDescription] = useState(initial?.description || '')
  const [freq, setFreq] = useState<RegulatoryFrequency>(initial?.frequency || 'MONTHLY')
  const [severity, setSeverity] = useState<EventSeverity>(initial?.severity || 'MEDIO')
  const [startDate, setStartDate] = useState(initial?.startDate?.slice(0, 10) || new Date().toISOString().slice(0, 10))
  const [endDate, setEndDate] = useState(initial?.endDate?.slice(0, 10) || '')
  const [startTime, setStartTime] = useState(initial?.startTime || '')
  const [endTime, setEndTime] = useState(initial?.endTime || '')
  const [notes, setNotes] = useState(initial?.notes || '')
  const [sendEmail, setSendEmail] = useState(initial?.sendEmail || false)
  const [isPersonalNote, setIsPersonalNote] = useState(initial?.isPersonalNote || false)
  const [sendReminderToAllUsers, setSendReminderToAllUsers] = useState(initial?.sendReminderToAllUsers || false)
  const [recipients, setRecipients] = useState<CalendarRecipient[]>(initial?.recipients || [])
  const [attachments, setAttachments] = useState<CalendarAttachment[]>(initial?.attachments || [])

  // Para destinatarios internos: cargar usuarios
  const { data: fetchedUsers } = useCalendarUsers()
  const allUsers = users.length > 0 ? users : (fetchedUsers || [])

  const [newRecipientKind, setNewRecipientKind] = useState<'INTERNAL' | 'EXTERNAL'>('INTERNAL')
  const [newInternalUserId, setNewInternalUserId] = useState('')
  const [newExternalEmail, setNewExternalEmail] = useState('')
  const [newExternalName, setNewExternalName] = useState('')

  const [newAttachmentName, setNewAttachmentName] = useState('')
  const [newAttachmentUrl, setNewAttachmentUrl] = useState('')

  const handleAddRecipient = () => {
    if (newRecipientKind === 'INTERNAL') {
      if (!newInternalUserId) return
      const user = allUsers.find(u => u.id === newInternalUserId)
      if (!user) return
      setRecipients(r => [...r, {
        id: `temp-${Date.now()}`,
        kind: 'INTERNAL',
        userId: user.id,
        user: { id: user.id, firstName: user.firstName, lastName: user.lastName, email: user.email },
      }])
      setNewInternalUserId('')
    } else {
      if (!newExternalEmail || !newExternalName) return
      setRecipients(r => [...r, {
        id: `temp-${Date.now()}`,
        kind: 'EXTERNAL',
        externalEmail: newExternalEmail,
        externalName: newExternalName,
      }])
      setNewExternalEmail('')
      setNewExternalName('')
    }
  }

  const handleAddAttachment = () => {
    if (!newAttachmentName) return
    setAttachments(a => [...a, {
      id: `temp-${Date.now()}`,
      filename: newAttachmentName,
      url: newAttachmentUrl || undefined,
    }])
    setNewAttachmentName('')
    setNewAttachmentUrl('')
  }

  const handleSubmit = () => {
    if (!title.trim()) return
    if (type === 'REGULATORY' && !entityId) return
    onSubmit({
      type,
      entityId: type === 'REGULATORY' ? entityId : null,
      title: title.trim(),
      description: description.trim() || undefined,
      frequency: freq,
      startDate,
      endDate: endDate || null,
      startTime: startTime || null,
      endTime: endTime || null,
      notes: notes.trim() || undefined,
      sendEmail,
      isPersonalNote,
      sendReminderToAllUsers,
      severity,
      recipients: recipients
        .filter(r => !r.id.startsWith('temp-') || true) // enviar todos
        .map(r => ({
          kind: r.kind,
          userId: r.userId,
          externalEmail: r.externalEmail,
          externalName: r.externalName,
        })),
      attachments: attachments
        .filter(a => !a.id.startsWith('temp-') || true)
        .map(a => ({
          filename: a.filename,
          url: a.url,
          localPath: a.localPath,
          mimeType: a.mimeType,
          size: a.size,
        })),
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 overflow-y-auto overflow-x-hidden">
      <div className="w-full max-w-3xl min-w-0 rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900 my-8">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-gray-100">
              {initial ? 'Editar evento' : 'Nuevo evento'}
            </h2>
            <p className="text-xs text-slate-500">Define el título, frecuencia, período y destinatarios</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Tipo de evento */}
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-gray-200">Tipo de evento</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {(Object.keys(EVENT_TYPE_LABELS) as RegulatoryEventType[]).map(t => {
                const Icon = eventTypeIcons[t]
                const active = type === t
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={cn(
                      'flex items-start gap-2 rounded-lg border p-3 text-left transition-colors',
                      active ? 'border-primary-500 bg-primary-50' : 'border-slate-200 hover:border-slate-300',
                    )}
                  >
                    <Icon className={cn('h-4 w-4 mt-0.5', active ? 'text-primary-600' : 'text-slate-500')} />
                    <div className="min-w-0 flex-1">
                      <p className={cn('text-xs font-semibold', active ? 'text-primary-700' : 'text-slate-700')}>
                        {EVENT_TYPE_LABELS[t]}
                      </p>
                      <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{EVENT_TYPE_DESCRIPTIONS[t]}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Entidad (solo si es REGULATORY) */}
          {type === 'REGULATORY' && (
            <label className="text-sm font-medium text-slate-700 dark:text-gray-200">
              Entidad regulatoria
              <select
                value={entityId}
                onChange={e => setEntityId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal"
              >
                <option value="">Seleccionar entidad</option>
                {entities.map(entity => (
                  <option key={entity.id} value={entity.id}>
                    {entity.acronym ? `${entity.acronym} · ` : ''}{entity.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Severidad */}
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-gray-200">Severidad</label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SEVERITY_OPTIONS.map(sev => {
                const SevIcon = severityIcons[sev]
                const active = severity === sev
                return (
                  <button
                    key={sev}
                    type="button"
                    onClick={() => setSeverity(sev)}
                    className={cn(
                      'flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors',
                      active
                        ? cn(SEVERITY_BADGE[sev], 'border-transparent')
                        : cn(SEVERITY_COLORS[sev], 'hover:opacity-80'),
                    )}
                  >
                    <SevIcon className="h-4 w-4" />
                    {SEVERITY_LABELS[sev]}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Título y descripción */}
          <label className="text-sm font-medium text-slate-700 dark:text-gray-200">
            Título
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Ej. Reporte mensual de nómina a la UGPP"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal"
            />
          </label>

          <label className="text-sm font-medium text-slate-700 dark:text-gray-200">
            Descripción
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              placeholder="Detalles adicionales, contexto, instrucciones…"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal"
            />
          </label>

          {/* Frecuencia y período */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm font-medium text-slate-700 dark:text-gray-200 min-w-0">
              Frecuencia
              <select
                value={freq}
                onChange={e => setFreq(e.target.value as RegulatoryFrequency)}
                className="mt-1 w-full min-w-0 rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal"
              >
                {FREQUENCY_OPTIONS.map(f => (
                  <option key={f} value={f}>{FREQUENCY_LABELS[f]}</option>
                ))}
              </select>
            </label>
            <div className="hidden sm:block" />
            <label className="text-sm font-medium text-slate-700 dark:text-gray-200 min-w-0">
              Fecha de inicio
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="mt-1 w-full min-w-0 rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal"
              />
            </label>
            <label className="text-sm font-medium text-slate-700 dark:text-gray-200 min-w-0">
              Fecha de fin
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="mt-1 w-full min-w-0 rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal"
              />
            </label>
            <label className="text-sm font-medium text-slate-700 dark:text-gray-200 min-w-0">
              Hora de inicio
              <input
                type="time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                className="mt-1 w-full min-w-0 rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal"
              />
            </label>
            <label className="text-sm font-medium text-slate-700 dark:text-gray-200 min-w-0">
              Hora de fin
              <input
                type="time"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                className="mt-1 w-full min-w-0 rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal"
              />
            </label>
          </div>

          {/* Destinatarios (si no es solo nota personal) */}
          {!isPersonalNote && (
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700 dark:text-gray-200 flex items-center gap-1.5">
                  <Users className="h-4 w-4" /> Destinatarios
                </label>
                <span className="text-xs text-slate-400">{recipients.length} agregado{recipients.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                <select
                  value={newRecipientKind}
                  onChange={e => setNewRecipientKind(e.target.value as any)}
                  className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm shrink-0"
                >
                  <option value="INTERNAL">Usuario de la app</option>
                  <option value="EXTERNAL">Correo externo</option>
                </select>
                {newRecipientKind === 'INTERNAL' ? (
                  <select
                    value={newInternalUserId}
                    onChange={e => setNewInternalUserId(e.target.value)}
                    className="flex-1 min-w-[180px] rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  >
                    <option value="">Seleccionar usuario…</option>
                    {allUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</option>
                    ))}
                  </select>
                ) : (
                  <>
                    <input
                      value={newExternalName}
                      onChange={e => setNewExternalName(e.target.value)}
                      placeholder="Nombre"
                      className="w-32 min-w-[120px] flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                    />
                    <input
                      value={newExternalEmail}
                      onChange={e => setNewExternalEmail(e.target.value)}
                      placeholder="email@dominio.com"
                      type="email"
                      className="flex-1 min-w-[180px] rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                    />
                  </>
                )}
                <button
                  type="button"
                  onClick={handleAddRecipient}
                  className="shrink-0 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-700"
                >
                  Agregar
                </button>
              </div>
              {recipients.length > 0 && (
                <div className="space-y-1">
                  {recipients.map(r => (
                    <div key={r.id} className="flex items-center justify-between gap-2 rounded border border-slate-100 bg-slate-50 px-2 py-1.5 text-xs">
                      <div className="flex items-center gap-2">
                        {r.kind === 'INTERNAL' ? <User className="h-3 w-3 text-blue-600" /> : <MailPlus className="h-3 w-3 text-purple-600" />}
                        <span className="font-medium">
                          {r.kind === 'INTERNAL' ? `${r.user?.firstName} ${r.user?.lastName}` : r.externalName}
                        </span>
                        <span className="text-slate-500">
                          ({r.kind === 'INTERNAL' ? r.user?.email : r.externalEmail})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setRecipients(rs => rs.filter(x => x.id !== r.id))}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Adjuntos */}
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700 dark:text-gray-200 flex items-center gap-1.5">
                <Paperclip className="h-4 w-4" /> Adjuntos
              </label>
              <span className="text-xs text-slate-400">{attachments.length} archivo{attachments.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              <input
                value={newAttachmentName}
                onChange={e => setNewAttachmentName(e.target.value)}
                placeholder="Nombre del archivo"
                className="flex-1 min-w-[160px] rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              />
              <input
                value={newAttachmentUrl}
                onChange={e => setNewAttachmentUrl(e.target.value)}
                placeholder="URL (opcional)"
                className="flex-1 min-w-[160px] rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              />
              <button
                type="button"
                onClick={handleAddAttachment}
                className="shrink-0 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Adjuntar
              </button>
            </div>
            {attachments.length > 0 && (
              <div className="space-y-1">
                {attachments.map(a => (
                  <div key={a.id} className="flex items-center justify-between gap-2 rounded border border-slate-100 bg-slate-50 px-2 py-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      <Paperclip className="h-3 w-3 text-slate-400" />
                      <span className="font-medium">{a.filename}</span>
                      {a.url && <span className="text-slate-500 truncate max-w-[200px]">{a.url}</span>}
                    </div>
                    <button
                      type="button"
                      onClick={() => setAttachments(as => as.filter(x => x.id !== a.id))}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Opciones adicionales */}
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={e => setSendEmail(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-primary-600"
              />
              <Mail className="h-4 w-4 text-slate-500" />
              <span className="text-slate-700">Enviar correo a destinatarios externos al ocurrir</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={sendReminderToAllUsers}
                onChange={e => setSendReminderToAllUsers(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-primary-600"
              />
              <Bell className="h-4 w-4 text-slate-500" />
              <span className="text-slate-700">Recordatorio a todos los usuarios de la app</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isPersonalNote}
                onChange={e => setIsPersonalNote(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-primary-600"
              />
              <StickyNote className="h-4 w-4 text-slate-500" />
              <span className="text-slate-700">Es solo una nota personal (sin envío ni recordatorio)</span>
            </label>
          </div>

          <label className="text-sm font-medium text-slate-700 dark:text-gray-200">
            Notas internas
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Notas para el equipo, contexto adicional…"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal"
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending || !title.trim() || (type === 'REGULATORY' && !entityId)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {initial ? 'Guardar cambios' : 'Crear evento'}
          </button>
        </div>
      </div>
    </div>
  )
}

interface EventDetailModalProps {
  event: RegulatoryEvent
  isPending: boolean
  onClose: () => void
  onComplete: () => void
  onEdit: () => void
  onDelete: () => void
}

function EventDetailModal({ event, isPending, onClose, onComplete, onEdit, onDelete }: EventDetailModalProps) {
  const Icon = eventTypeIcons[event.type] || CalendarDays
  const isOverdue = event.computedStatus === 'OVERDUE'
  const severity = event.severity || 'MEDIO'

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 overflow-y-auto overflow-x-hidden">
      <div className="w-full max-w-2xl min-w-0 rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900 my-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border',
              isOverdue ? 'bg-red-50 text-red-600 border-red-200' : eventTypeColors[event.type],
            )}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">
                  {EVENT_TYPE_LABELS[event.type]}
                </p>
                <span className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide',
                  SEVERITY_BADGE[severity],
                )}>
                  {SEVERITY_LABELS[severity]}
                </span>
              </div>
              <h2 className="mt-1 text-lg font-bold text-slate-900 dark:text-gray-100">{event.title}</h2>
              {event.entity && (
                <p className="text-xs text-slate-500">{event.entity.acronym || event.entity.name}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3 text-sm">
          {event.description && (
            <p className="rounded-lg bg-slate-50 p-3 text-slate-600">{event.description}</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Info label="Frecuencia" value={FREQUENCY_LABELS[event.frequency]} />
            <Info label="Severidad" value={SEVERITY_LABELS[severity]} />
            <Info label="Estado" value={statusLabels[event.computedStatus || event.status]} red={isOverdue} />
            <Info label="Fecha de inicio" value={new Date(event.startDate).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })} />
            {event.endDate && (
              <Info label="Fecha de fin" value={new Date(event.endDate).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })} />
            )}
            {event.startTime && <Info label="Hora" value={`${event.startTime}${event.endTime ? ` - ${event.endTime}` : ''}`} />}
            <Info label="Próximo vencimiento" value={new Date(event.nextDueDate).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })} red={isOverdue} />
          </div>

          {/* Destinatarios */}
          {event.recipients && event.recipients.length > 0 && (
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                <Users className="h-3 w-3" /> Destinatarios ({event.recipients.length})
              </p>
              <div className="space-y-1">
                {event.recipients.map(r => (
                  <div key={r.id} className="flex items-center gap-2 text-xs">
                    {r.kind === 'INTERNAL' ? <User className="h-3 w-3 text-blue-600" /> : <MailPlus className="h-3 w-3 text-purple-600" />}
                    <span className="font-medium">
                      {r.kind === 'INTERNAL' ? `${r.user?.firstName} ${r.user?.lastName}` : r.externalName}
                    </span>
                    <span className="text-slate-500">
                      ({r.kind === 'INTERNAL' ? r.user?.email : r.externalEmail})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Adjuntos */}
          {event.attachments && event.attachments.length > 0 && (
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                <Paperclip className="h-3 w-3" /> Adjuntos ({event.attachments.length})
              </p>
              <div className="space-y-1">
                {event.attachments.map(a => (
                  <div key={a.id} className="flex items-center gap-2 text-xs">
                    <Paperclip className="h-3 w-3 text-slate-400" />
                    <span className="font-medium">{a.filename}</span>
                    {a.url && (
                      <a href={a.url} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline truncate max-w-[300px]">
                        {a.url}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {event.notes && (
            <div className="rounded-lg bg-amber-50 p-3 text-slate-700 border border-amber-200">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-1">Notas</p>
              {event.notes}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-between gap-2">
          <button onClick={onDelete} disabled={isPending} className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
            Cancelar evento
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600">
              Cerrar
            </button>
            <button onClick={onEdit} disabled={isPending} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
              Editar
            </button>
            {event.status !== 'COMPLETED' && event.status !== 'CANCELLED' && (
              <button onClick={onComplete} disabled={isPending} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                <CheckCircle2 className="h-4 w-4" />
                Marcar cumplido
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface DayEventsPopoverProps {
  day: Date
  events: RegulatoryEvent[]
  anchor: { top: number; left: number }
  onClose: () => void
  onSelectEvent: (event: RegulatoryEvent) => void
}

function DayEventsPopover({ day, events, anchor, onClose, onSelectEvent }: DayEventsPopoverProps) {
  const sorted = [...events].sort((a, b) => (SEVERITY_RANK[b.severity || 'MEDIO'] ?? 0) - (SEVERITY_RANK[a.severity || 'MEDIO'] ?? 0))
  const dayLabel = day.toLocaleDateString('es-CO', { weekday: 'long', day: '2-digit', month: 'long' })

  // Ajustar posición para que no se salga de la pantalla
  const maxWidth = 320
  const left = Math.min(anchor.left, window.innerWidth - maxWidth - 16)
  const top = anchor.top

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 rounded-lg border border-slate-200 bg-white shadow-xl"
        style={{ top, left, width: maxWidth, maxHeight: 400 }}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 bg-slate-50 rounded-t-lg">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-700 capitalize">{dayLabel}</p>
            <p className="text-[11px] text-slate-500">{events.length} evento{events.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: 348 }}>
          {sorted.map(event => {
            const Icon = eventTypeIcons[event.type] || CalendarDays
            const isOverdue = event.computedStatus === 'OVERDUE'
            const severity = event.severity || 'MEDIO'
            return (
              <button
                key={event.id}
                onClick={() => onSelectEvent(event)}
                className={cn(
                  'flex w-full items-start gap-2 border-b border-slate-100 px-3 py-2 text-left hover:bg-slate-50 transition-colors last:border-b-0',
                  isOverdue && 'bg-red-50/40',
                )}
              >
                <div className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border',
                  isOverdue ? 'bg-red-50 text-red-600 border-red-200' : eventTypeColors[event.type],
                )}>
                  <Icon className="h-4 w-4 shrink-0" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-xs font-semibold text-slate-800">{event.title}</p>
                    <span className={cn(
                      'inline-flex shrink-0 items-center px-1 py-0 rounded text-[9px] font-bold uppercase tracking-wide',
                      SEVERITY_BADGE[severity],
                    )}>
                      {SEVERITY_LABELS[severity].substring(0, 4)}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[10px] text-slate-500 truncate">
                    {event.entity?.acronym || event.entity?.name || EVENT_TYPE_LABELS[event.type]}
                    {' · '}
                    {FREQUENCY_LABELS[event.frequency]}
                    {event.startTime && ` · ${event.startTime}`}
                  </p>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </>
  )
}

function Info({ label, value, red }: { label: string; value: string; red?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-slate-500">{label}</span>
      <strong className={cn('text-slate-800', red && 'text-red-600')}>{value}</strong>
    </div>
  )
}